import { apiPrefix } from '../config'
import { createRequestor } from '../api/makeRequest'
import { UseRequestStatus, useRequest } from '../api/useRequest'
import { useLocale } from '../locale'


const authApiPrefix = `${apiPrefix}/auth`
export const loginUri = `${authApiPrefix}/login`
const authRequestor = createRequestor(authApiPrefix)

export type AuthInfo = {
  provider: 'oauth' | 'test',
  testAuthenticationEnabled: boolean,
}

export type TestLoginUser = {
  username: string,
  titlesPre: string,
  firstName: string,
  lastName: string,
  titlesPost: string,
  roles: string[],
  studies?: Array<{
    id: number,
    active: boolean,
    specialization?: {
      code: string,
    } | null,
  }>,
}

type TestLoginPayload = {
  username: string,
  developerLogin?: string,
}

export async function getAuthInfo (): Promise<AuthInfo> {
  const [, authInfo] = await authRequestor<AuthInfo>('GET', 'info')

  if (!authInfo) {
    throw new Error('Auth info response is empty')
  }

  return authInfo
}

export function useAuthInfo (): [
  UseRequestStatus<AuthInfo>,
  () => Promise<void>,
] {
  const [requestStatus, sendRequest] = useRequest<AuthInfo>(authRequestor, {
    defaultErrorMessage: () => 'An error has occurred while loading authentication information',
  })

  return [requestStatus, async () => await sendRequest('GET', 'info')]
}

export function useTestLoginUsers (): [
  UseRequestStatus<TestLoginUser[]>,
  () => Promise<void>,
] {
  const [requestStatus, sendRequest] = useRequest<TestLoginUser[]>(authRequestor, {
    defaultErrorMessage: () => 'An error has occurred while loading test users',
  })

  return [requestStatus, async () => await sendRequest('GET', 'test-users')]
}

export function useTestLogin (): [
  UseRequestStatus<void>,
  (payload: TestLoginPayload) => Promise<void>,
] {
  const [requestStatus, sendRequest] = useRequest<void>(authRequestor, {
    defaultErrorMessage: () => 'An error has occurred while logging in',
  })

  return [requestStatus, async (payload) => await sendRequest('POST', 'test-login', {}, {
    body: JSON.stringify(payload),
  })]
}

/**
 * Exchanges authorization code for an access token from OAAS.
 * */
export function useGetToken (): [
  UseRequestStatus<void>,
  (code: string, state: string) => void,
] {
  const { l } = useLocale()

  const [requestStatus, sendRequest] = useRequest<void>(authRequestor, {
    defaultErrorMessage: () => l.auth.errorRetrievingOAuthToken,
  })

  return [requestStatus, (code, state) => {
    void sendRequest('GET', 'token', {
      code,
      state,
    })
  }]
}

export function useLogout (): [
  UseRequestStatus<void>,
  () => void,
] {
  const { l } = useLocale()

  const [requestStatus, sendRequest] = useRequest<void>(authRequestor, {
    defaultErrorMessage: () => l.auth.errorLoggingOut,
  })

  return [requestStatus, () => {
    void sendRequest('POST', 'logout')
  }]
}

const redirectToKeyName = 'redirectTo'
export const authRedirectToPathUtils = {
  set: (path: string): void => window.sessionStorage.setItem(redirectToKeyName, path),
  get: (): string | null => window.sessionStorage.getItem(redirectToKeyName),
  clear: (): void => window.sessionStorage.removeItem(redirectToKeyName),
}

function goToAuth (currentPath?: string): void {
  if (currentPath) {
    authRedirectToPathUtils.set(currentPath)
  }

  const form = document.createElement('form')
  form.method = 'post'
  form.action = loginUri
  form.id = 'auth-redirect-form' // FIXME: Probably redundant.
  document.body.append(form)
  form.submit()

  document.body.removeChild(form) // FIXME: Probably redundant.
}

function goToTestAuth (currentPath?: string): void {
  if (currentPath) {
    authRedirectToPathUtils.set(currentPath)
  }

  const publicPath = process.env.PUBLIC_PATH
  const basename = publicPath && publicPath !== '/'
    ? publicPath.replace(/\/+$/, '')
    : ''

  window.location.assign(`${basename}/test-login`)
}

export function goToAuthSaveCurrentLocation (): void {
  const currentPath = window.location.pathname + window.location.search + window.location.hash

  void getAuthInfo()
    .then(authInfo => authInfo.testAuthenticationEnabled ? goToTestAuth(currentPath) : goToAuth(currentPath))
    .catch(() => goToAuth(currentPath))
}
